/**
 * 系统级数据选择器模块
 *
 * 提供统一的数据选择组件，可在整个系统中复用。
 * 例如：邮件发送时选择航班、批量操作时选择数据、报表筛选等。
 *
 * 使用方式：
 * 1. 直接使用具体选择器：<OperationFlightSelector value={...} onChange={...} />
 * 2. 通过工厂组件动态创建：<DataSelectorFactory type="operationFlight" ... />
 * 3. 根据内容块类型获取：getDataSelectorByContentBlock("operationFlightList")
 */

import React from "react";

// ============ 选择器组件导入 ============
// 注意：使用懒加载避免循环依赖
const OperationFlightSelector = React.lazy(() => import("./OperationFlightSelector"));
const ScheduleFlightSelector = React.lazy(() => import("./ScheduleFlightSelector"));

// ============ 选择器注册表 ============
/**
 * 数据选择器注册表
 * 每种选择器需要注册以下信息：
 * - component: 选择器组件
 * - label: 显示名称
 * - description: 描述文字
 * - icon: 图标名称（Ant Design Icons）
 */
export const DATA_SELECTOR_REGISTRY = {
    // ===== 航班相关 =====
    operationFlight: {
        component: OperationFlightSelector,
        label: "执行航班",
        description: "选择执行航班数据",
        icon: "RocketOutlined",
    },
    scheduleFlight: {
        component: ScheduleFlightSelector,
        label: "计划航班",
        description: "选择计划航班数据",
        icon: "CalendarOutlined",
    },

    // ===== 未来扩展 =====
    // supplier: { ... },
    // aircraft: { ... },
    // airport: { ... },
};

// ============ 内容块类型映射 ============
/**
 * 邮件模板内容块类型 → 数据选择器类型 映射
 * 用于根据模板的 content_blocks 自动选择合适的数据选择器
 */
export const CONTENT_BLOCK_TO_SELECTOR = {
    operationFlightList: "operationFlight",
    scheduleFlightList: "scheduleFlight",
    // 未来扩展...
};

// ============ 工具函数 ============

/**
 * 根据选择器类型获取选择器配置
 * @param {string} type - 选择器类型
 * @returns {object|null} 选择器配置
 */
export function getDataSelectorConfig(type) {
    return DATA_SELECTOR_REGISTRY[type] || null;
}

/**
 * 根据内容块类型获取对应的选择器类型
 * @param {string} contentBlockType - 内容块类型
 * @returns {string|null} 选择器类型
 */
export function getSelectorTypeByContentBlock(contentBlockType) {
    return CONTENT_BLOCK_TO_SELECTOR[contentBlockType] || null;
}

/**
 * 根据模板的 content_blocks 数组，推断需要哪种选择器
 * @param {string[]} contentBlocks - 模板的内容块类型数组
 * @returns {string|null} 选择器类型（优先返回第一个匹配的）
 */
export function inferSelectorFromContentBlocks(contentBlocks) {
    if (!contentBlocks || !Array.isArray(contentBlocks)) return null;

    for (const blockType of contentBlocks) {
        const selectorType = CONTENT_BLOCK_TO_SELECTOR[blockType];
        if (selectorType) {
            return selectorType;
        }
    }
    return null;
}

/**
 * 获取所有已注册的选择器类型列表
 * @returns {string[]} 选择器类型数组
 */
export function getAllSelectorTypes() {
    return Object.keys(DATA_SELECTOR_REGISTRY);
}

// ============ 组件导出 ============
export { default as DataSelectorFactory } from "./DataSelectorFactory";
// 具体选择器按需导入，避免循环依赖
